import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BULK_CREATE_MARK } from '../domain/bulkSelection';
import { DRAFT_SYNC_LABELS, draftSyncState } from '../domain/drafts';
import type { UnsyncedDraft } from '../domain/drafts';
import type { Listing } from '../domain/listings';
import { useDraftOutbox, useUnsyncedDrafts } from '../hooks/useDraftOutbox';
import { useObjectUrl } from '../hooks/useObjectUrl';
import { useOnline, useRetryWhenBackOnline } from '../hooks/useOnline';
import { usePagedLoad } from '../hooks/usePagedLoad';
import { useRepositories } from '../hooks/useRepositories';
import type { EditOrigin } from '../utils/listings';
import PhotoThumb from '../components/PhotoThumb';
import {
  ActionError,
  EmptyState,
  ErrorState,
  LoadingState,
  StateForError,
} from '../components/PageStates';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2,
  ChevronLeft,
  CircleAlert,
  CloudOff,
  FilePen,
  Images,
  Package,
  RefreshCw,
} from 'lucide-react';

const DRAFTS_PATH = '/my-listings/drafts';

/** A draft made on this page: still unsynced, or synced since. */
type PageDraft =
  | { kind: 'unsynced'; draft: UnsyncedDraft; createdAt: number; seq: number }
  | { kind: 'synced'; listing: Listing; createdAt: number; seq: number };

/**
 * `/my-listings/drafts` — all my drafts (021 FR-011): the ones this page made
 * that the service doesn't hold yet, each with its sync state in FR-009's
 * words (FR-009a), then the service's, newest first. Each shows its first
 * photo and its title or "Untitled". Drafts made on this page keep the order
 * they were made in — a batch in the order it was reviewed — before and after
 * they sync, not the order the service made them. A draft that syncs while
 * shown keeps its place and loses its marker, with no gap (R5). A draft opens in the
 * editor, which comes back here after saving or publishing (FR-011c).
 */
export default function Drafts() {
  const { listings } = useRepositories();
  const sync = useDraftOutbox();
  const unsynced = useUnsyncedDrafts();
  const online = useOnline();
  const created = useCreatedOnce();
  const { first, items, hasMore, loadMore, loadingMore, moreError } = usePagedLoad(
    (page) => listings.mine(page, 'draft'),
    'mine-drafts',
  );

  // The service's copy of each draft that syncs while shown, until the list has it.
  const [syncedHere, setSyncedHere] = useState<Listing[]>([]);
  useEffect(
    () =>
      sync.onSynced((listing) =>
        setSyncedHere((list) => [...list.filter((l) => l.id !== listing.id), listing]),
      ),
    [sync],
  );

  // How long Create took to show this view (NFR-001).
  useEffect(() => {
    if (performance.getEntriesByName(BULK_CREATE_MARK).length === 0) return;
    performance.measure('bulk.create', BULK_CREATE_MARK);
    performance.clearMarks(BULK_CREATE_MARK);
  }, []);

  // Offline, the service's drafts didn't load; try again once back.
  useRetryWhenBackOnline(first.status === 'error', first.reload);

  const { outbox } = sync;
  const pageDrafts = useMemo(() => {
    const entries = new Map<string, PageDraft>();
    for (const listing of [...items, ...syncedHere]) {
      const place = outbox.placeOf(listing.id);
      if (place) entries.set(listing.id, { kind: 'synced', listing, ...place });
    }
    for (const draft of unsynced) {
      const place = outbox.placeOf(draft.id) ?? { createdAt: draft.createdAt, seq: 0 };
      entries.set(draft.id, { kind: 'unsynced', draft, ...place });
    }
    // Newest batch first; within a batch, the order it was reviewed in.
    return [...entries.values()].sort((a, b) => b.createdAt - a.createdAt || a.seq - b.seq);
  }, [outbox, items, unsynced, syncedHere]);
  const shownHere = new Set(pageDrafts.map(idOf));
  const fromService = items.filter((l) => !shownHere.has(l.id));

  const onlyService = pageDrafts.length === 0;

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      <div className="space-y-2">
        <Link
          to="/my-listings"
          className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="size-4" />
          My listings
        </Link>
        <h1 className="text-4xl font-bold tracking-tight text-foreground">Drafts</h1>
        {created !== undefined && created > 0 && (
          <p className="flex items-center gap-2 text-sm font-medium text-foreground">
            <CheckCircle2 className="size-4 text-primary" aria-hidden="true" />
            {created === 1 ? '1 draft created' : `${created} drafts created`}
          </p>
        )}
      </div>
      {onlyService && fromService.length === 0 ? (
        first.status === 'ready' && (
          <EmptyState
            icon={<FilePen className="size-8" />}
            title="No drafts"
            description="Listings you save as drafts wait here. Only you can see them."
            action={
              <Button variant="secondary" className="rounded-full" asChild>
                <Link to="/listings/new-from-photos">
                  <Images />
                  New from photos
                </Link>
              </Button>
            }
          />
        )
      ) : (
        <ul aria-label="Drafts" className="space-y-3">
          {pageDrafts.map((entry) =>
            entry.kind === 'unsynced' ? (
              <li key={entry.draft.id}>
                <UnsyncedDraftRow draft={entry.draft} online={online} />
              </li>
            ) : (
              <li key={entry.listing.id}>
                <DraftRow draft={entry.listing} />
              </li>
            ),
          )}
          {fromService.map((draft) => (
            <li key={draft.id}>
              <DraftRow draft={draft} />
            </li>
          ))}
        </ul>
      )}
      {first.status === 'loading' && (
        <LoadingState label={onlyService ? 'Loading your drafts' : 'Loading your other drafts'} />
      )}
      {first.status === 'error' &&
        (!online ? (
          <p className="flex items-center justify-center gap-2 text-center text-sm text-muted-foreground">
            <CloudOff className="size-4 shrink-0" aria-hidden="true" />
            {onlyService
              ? "You're offline. Your drafts show here once you're back online."
              : "Your other drafts show here once you're back online."}
          </p>
        ) : onlyService ? (
          <StateForError error={first.error} onRetry={first.reload} />
        ) : (
          <ErrorState message="Couldn't load your other drafts." onRetry={first.reload} />
        ))}
      {moreError !== null && <ActionError error={moreError} />}
      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            className="rounded-full"
            disabled={loadingMore}
            onClick={loadMore}
          >
            {loadingMore ? 'Loading…' : 'Show more'}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * How many drafts New from photos just made, said once: the count leaves the
 * history entry as soon as it's read, so a reload or coming Back doesn't
 * announce it again.
 */
function useCreatedOnce(): number | undefined {
  const location = useLocation();
  const navigate = useNavigate();
  const [created] = useState(() => (location.state as { created?: number } | null)?.created);
  const hasState = location.state !== null && location.state !== undefined;
  useEffect(() => {
    if (created !== undefined && hasState) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [created, hasState, navigate, location.pathname, location.search]);
  return created;
}

function idOf(entry: PageDraft): string {
  return entry.kind === 'unsynced' ? entry.draft.id : entry.listing.id;
}

const ROW_CLASS =
  'flex items-center gap-4 rounded-3xl bg-card p-3 transition-colors hover:bg-secondary';
const ORIGIN: EditOrigin = { from: DRAFTS_PATH };

function DraftRow({ draft }: { draft: Listing }) {
  return (
    <Link to={`/listings/${draft.id}/edit`} state={ORIGIN} className={ROW_CLASS}>
      <Cover url={draft.photos[0]?.url} />
      <div className="flex min-w-0 flex-col gap-1">
        <Title title={draft.title} />
        <PhotoCount n={draft.photos.length} />
      </div>
    </Link>
  );
}

function UnsyncedDraftRow({ draft, online }: { draft: UnsyncedDraft; online: boolean }) {
  const cover = useObjectUrl(draft.photos[0]?.file);
  const state = draftSyncState(draft, online);
  const label = DRAFT_SYNC_LABELS[state];
  const title = draft.fields.title.trim();
  const Icon =
    state === 'syncing' ? RefreshCw : state === 'waitingForConnection' ? CloudOff : CircleAlert;
  return (
    <Link
      to={`/listings/${draft.id}/edit`}
      state={ORIGIN}
      aria-label={[title || 'Untitled', photoCount(draft.photos.length), label]
        .filter(Boolean)
        .join(', ')}
      className={ROW_CLASS}
    >
      <Cover url={cover} />
      <div className="flex min-w-0 flex-col gap-1">
        <Title title={draft.fields.title} />
        <PhotoCount n={draft.photos.length} />
        <span
          className={
            state === 'syncing' || state === 'waitingForConnection'
              ? 'flex items-center gap-1 text-sm text-muted-foreground'
              : 'flex items-center gap-1 text-sm font-medium text-destructive'
          }
        >
          <Icon className="size-3.5" aria-hidden="true" />
          {label}
        </span>
      </div>
    </Link>
  );
}

function Cover({ url }: { url: string | undefined }) {
  if (url) return <PhotoThumb url={url} className="size-16" />;
  return (
    <div className="flex size-16 shrink-0 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
      <Package className="size-6" />
    </div>
  );
}

/** "3 photos" for a draft with more than one; nothing otherwise. */
function photoCount(n: number): string | null {
  return n > 1 ? `${n} photos` : null;
}

function PhotoCount({ n }: { n: number }) {
  const text = photoCount(n);
  return text ? <span className="text-sm text-muted-foreground">{text}</span> : null;
}

function Title({ title }: { title: string }) {
  const untitled = !title.trim();
  return (
    <span
      className={
        untitled
          ? 'line-clamp-1 pr-1 font-semibold italic text-muted-foreground'
          : 'line-clamp-1 font-semibold text-foreground'
      }
    >
      {untitled ? 'Untitled' : title}
    </span>
  );
}
